import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { sumExpensesByPhaseId, sumPlannedByPhaseId } from "@/lib/utils/budget";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { indexPhaseRow } from "./knowledge";

export type Phase = Tables<"phases">;
export type PhaseInsert = Insert<"phases">;
export type PhaseUpdate = Update<"phases">;
export type PhaseWithStats = Phase & {
  taskCount: number;
  taskDoneCount: number;
  /** Sum of expenses linked to the phase (not task.budget_actual). */
  budgetActualTotal: number;
  /** Sum of `tasks.budget_planned` — a fázisnak nincs saját tervezett mezője. */
  budgetPlannedTotal: number;
};

export async function listPhases(projectId?: string): Promise<Phase[]> {
  let query = supabase.from("phases").select("*").order("order_index", { ascending: true });
  if (projectId) query = query.eq("project_id", projectId);
  const { data, error } = await query;
  throwIfError(error);
  return excludeTestData(data);
}

export async function getPhase(id: string): Promise<Phase> {
  const { data, error } = await supabase.from("phases").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Phase not found");
}

const PHASE_INDEX_FIELDS = ["title", "description", "guide"] as const;

function shouldReindexPhase(values: PhaseInsert | PhaseUpdate): boolean {
  return PHASE_INDEX_FIELDS.some((k) => Object.prototype.hasOwnProperty.call(values, k));
}

function schedulePhaseIndex(phase: Phase): void {
  void indexPhaseRow(phase).catch(() => {
    // Indexelés best-effort — a mentés már sikerült.
  });
}

export async function createPhase(values: PhaseInsert): Promise<Phase> {
  const { data, error } = await supabase.from("phases").insert(values).select("*").single();
  throwIfError(error);
  const row = assertData(data);
  schedulePhaseIndex(row);
  return row;
}

export async function updatePhase(id: string, values: PhaseUpdate): Promise<Phase> {
  const { data, error } = await supabase
    .from("phases")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  const row = assertData(data, "Phase not found");
  if (shouldReindexPhase(values)) schedulePhaseIndex(row);
  return row;
}

export async function deletePhase(id: string): Promise<void> {
  const { error } = await supabase.from("phases").delete().eq("id", id);
  throwIfError(error);
}

export async function listPhasesWithStats(projectId?: string): Promise<PhaseWithStats[]> {
  const phases = await listPhases(projectId);
  if (phases.length === 0) return [];

  const withFlag = await Promise.all([
    supabase.from("tasks").select("id,phase_id,status,budget_planned,is_test"),
    supabase.from("expenses").select("amount,phase_id,task_id,is_test")
  ]);
  const missingFlag =
    (withFlag[0].error && /is_test/i.test(withFlag[0].error.message)) ||
    (withFlag[1].error && /is_test/i.test(withFlag[1].error.message));
  const taskRes = missingFlag
    ? await supabase.from("tasks").select("id,phase_id,status,budget_planned")
    : withFlag[0];
  const expenseRes = missingFlag
    ? await supabase.from("expenses").select("amount,phase_id,task_id")
    : withFlag[1];
  throwIfError(taskRes.error);
  throwIfError(expenseRes.error);
  const liveTasks = (taskRes.data ?? []).filter((t) => (t as { is_test?: boolean | null }).is_test !== true);
  const liveExpenses = (expenseRes.data ?? []).filter(
    (e) => (e as { is_test?: boolean | null }).is_test !== true
  );

  const taskPhaseById = new Map<string, string | null>();
  const stats = new Map<string, { taskCount: number; taskDoneCount: number }>();
  for (const t of liveTasks) {
    taskPhaseById.set(t.id, t.phase_id);
    if (!t.phase_id) continue;
    const v = stats.get(t.phase_id) ?? { taskCount: 0, taskDoneCount: 0 };
    v.taskCount += 1;
    if (t.status === "done") v.taskDoneCount += 1;
    stats.set(t.phase_id, v);
  }

  const spentByPhase = sumExpensesByPhaseId(liveExpenses, taskPhaseById);
  const plannedByPhase = sumPlannedByPhaseId(liveTasks);

  return phases.map((p) => {
    const v = stats.get(p.id) ?? { taskCount: 0, taskDoneCount: 0 };
    return {
      ...p,
      taskCount: v.taskCount,
      taskDoneCount: v.taskDoneCount,
      budgetActualTotal: spentByPhase.get(p.id) ?? 0,
      budgetPlannedTotal: plannedByPhase.get(p.id) ?? 0
    };
  });
}

export const phasesQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: [...qk.phases, { projectId }] as const,
    queryFn: () => listPhases(projectId)
  });

export const phasesWithStatsQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: ["phases", "withStats", { projectId }] as const,
    queryFn: () => listPhasesWithStats(projectId)
  });

export const phaseQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.phase(id),
    queryFn: () => getPhase(id)
  });

